import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Link,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import type { SvgIconComponent } from '@mui/icons-material';
import { MealSuggestionsClientSummaryStrip } from '@/components/meal-planning/manual/MealSuggestionsClientSummaryStrip';
import { Tag } from '@/components/ui/tag';
import type { JsonQuestion } from '@/components/questionnaires/json-driven/types';
import {
  getQuestionnaireInstanceResponse,
  type QuestionnaireInstanceDetailResponse,
  type QuestionnaireResponseItem,
} from '@/lib/api/questionnaires';
import { buildSectionGroups } from '@/pages/dashboard/questionnaire-final-detail/utils';
import type { Client } from '@/types/client';
import { formatQuestionnaireAnswerValue } from '@/utils/questionnaire-answer-display';

interface SectionAppearance {
  label: string;
  order: number;
  color: string;
  iconBg: string;
  iconColor: string;
  icon: SvgIconComponent;
}

interface PreviewResponseItem extends QuestionnaireResponseItem {
  index: number;
  questionTextResolved: string;
  typeResolved: string;
  answerPreview: string;
}

interface PreviewResponseGroup {
  id: string;
  label: string;
  order: number;
  color: string;
  iconBg: string;
  iconColor: string;
  icon: SvgIconComponent;
  items: PreviewResponseItem[];
}

function toSectionKey(value?: string): string {
  const normalized = value?.replace(/\s+/g, ' ').trim();
  return normalized && normalized.length > 0 ? normalized.toLowerCase() : 'additional questions';
}

function normalizeSectionLabel(value?: string): string {
  const normalized = value?.replace(/\s+/g, ' ').trim();
  return normalized && normalized.length > 0 ? normalized : 'Additional Questions';
}

function formatQuestionType(type?: string): string {
  if (!type) return 'Unknown';
  const normalizedType = type.replace(/_/g, '-');
  if (normalizedType === 'select') return 'Select one';
  if (normalizedType === 'multi-select') return 'Select all that apply';
  return type
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDateLabel(value?: string): string {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
}

function buildSectionAppearanceLookup(responses: QuestionnaireResponseItem[]): Map<string, SectionAppearance> {
  const map = new Map<string, SectionAppearance>();
  const seen = new Set<string>();

  responses.forEach((response, index) => {
    const sectionLabel = normalizeSectionLabel(response.label);
    const sectionKey = toSectionKey(sectionLabel);
    if (seen.has(sectionKey)) return;
    seen.add(sectionKey);

    const syntheticQuestion: JsonQuestion = {
      _id: response.questionId,
      questionText: response.questionText,
      type: response.type as JsonQuestion['type'],
      label: sectionLabel,
      order: index,
    };
    const group = buildSectionGroups([syntheticQuestion])[0];
    map.set(sectionKey, {
      label: group?.label ?? sectionLabel,
      order: map.size,
      color: group?.color ?? '#6b7a8f',
      iconBg: group?.iconBg ?? '#edf3f8',
      iconColor: group?.iconColor ?? '#6b7a8f',
      icon: group?.icon ?? DescriptionRoundedIcon,
    });
  });

  return map;
}

function buildClientSummaryFromInstance(
  instance: QuestionnaireInstanceDetailResponse,
  clientNameOverride?: string,
): Client | null {
  const clientId = instance.clientId?.trim();
  if (!clientId) return null;

  const assigned = instance.assignedClient;
  const displayName = clientNameOverride?.trim() || 'Client';
  const nameParts = displayName.split(/\s+/).filter(Boolean);

  return {
    id: clientId,
    clientId,
    fullName: displayName,
    firstName: nameParts[0] ?? displayName,
    lastName: nameParts.slice(1).join(' '),
    dateOfBirth: assigned?.dateOfBirth ?? '',
    gender: assigned?.gender ?? '',
    activityLevel: assigned?.activityLevel ?? '',
    allergies: assigned?.allergies ?? [],
    foodIntolerances: assigned?.foodIntolerances ?? [],
    medicalConditions: Array.isArray(assigned?.medicalConditions)
      ? assigned.medicalConditions.join(', ')
      : '',
  } as Client;
}

const QuestionnaireResponsePreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { questionnaireId, instanceId } = useParams<{ questionnaireId: string; instanceId: string }>();
  const questionnaireRouteId = questionnaireId as string;
  const instanceRouteId = instanceId as string;

  const clientIdFromQuery = searchParams.get('clientId') ?? '';
  const clientNameFromQuery = searchParams.get('clientName') ?? '';

  const {
    data: instanceResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['questionnaire-preview-response', instanceRouteId],
    queryFn: () => getQuestionnaireInstanceResponse(instanceRouteId),
    enabled: Boolean(instanceRouteId),
  });

  const flattenedResponses = React.useMemo(() => {
    if (!instanceResponse?.submissions?.length) return [] as QuestionnaireResponseItem[];
    return instanceResponse.submissions.flatMap((submission) => submission.responses ?? []);
  }, [instanceResponse]);

  const submittedNotes = React.useMemo(() => {
    const fromSubmissions =
      instanceResponse?.submissions
        ?.map((submission) => submission.notes?.trim())
        .filter((value): value is string => Boolean(value)) ?? [];
    if (fromSubmissions.length > 0) {
      return fromSubmissions.join('\n\n');
    }
    return instanceResponse?.notes?.trim() || '';
  }, [instanceResponse]);

  const sectionAppearanceLookup = React.useMemo(
    () => buildSectionAppearanceLookup(flattenedResponses),
    [flattenedResponses],
  );

  const groupedResponses = React.useMemo(() => {
    if (flattenedResponses.length === 0) return [] as PreviewResponseGroup[];

    const groups = new Map<string, PreviewResponseGroup>();
    const sectionOrder: string[] = [];

    flattenedResponses.forEach((response, index) => {
      const sectionLabel = normalizeSectionLabel(response.label);
      const sectionKey = toSectionKey(sectionLabel);
      const appearance = sectionAppearanceLookup.get(sectionKey);

      if (!groups.has(sectionKey)) {
        sectionOrder.push(sectionKey);
        groups.set(sectionKey, {
          id: `section-${sectionKey.replace(/[^a-z0-9]+/g, '-') || 'general'}`,
          label: appearance?.label ?? sectionLabel,
          order: appearance?.order ?? sectionOrder.length - 1,
          color: appearance?.color ?? '#6b7a8f',
          iconBg: appearance?.iconBg ?? '#edf3f8',
          iconColor: appearance?.iconColor ?? '#6b7a8f',
          icon: appearance?.icon ?? DescriptionRoundedIcon,
          items: [],
        });
      }

      groups.get(sectionKey)?.items.push({
        ...response,
        index,
        questionTextResolved: response.questionText,
        typeResolved: response.type,
        answerPreview: formatQuestionnaireAnswerValue(response.answer, {
          type: response.type,
          otherText: response.otherText,
        }),
      });
    });

    return sectionOrder
      .map((sectionKey) => groups.get(sectionKey))
      .filter((group): group is PreviewResponseGroup => Boolean(group));
  }, [flattenedResponses, sectionAppearanceLookup]);

  const [expandedGroups, setExpandedGroups] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (groupedResponses.length === 0) {
      setExpandedGroups([]);
      return;
    }

    setExpandedGroups((previous) => {
      const validGroups = previous.filter((groupId) => groupedResponses.some((group) => group.id === groupId));
      if (validGroups.length > 0) return validGroups;
      return [groupedResponses[0].id];
    });
  }, [groupedResponses]);

  const toggleGroup = React.useCallback((groupId: string) => {
    setExpandedGroups((previous) =>
      previous.includes(groupId) ? previous.filter((id) => id !== groupId) : [...previous, groupId]
    );
  }, []);

  const sourcePath = `/questionnaires/${encodeURIComponent(questionnaireRouteId)}/responses`;
  const resolvedClientId = clientIdFromQuery || instanceResponse?.clientId || '';
  const client = instanceResponse
    ? buildClientSummaryFromInstance(instanceResponse, clientNameFromQuery || undefined)
    : null;
  const questionnaireTitle =
    instanceResponse?.questionnaireTitle?.trim()
    || instanceResponse?.questionnaireName?.trim()
    || 'Questionnaire Response';
  const headerDateLabel = formatDateLabel(
    instanceResponse?.completedAt ?? instanceResponse?.submissions?.[0]?.submittedAt,
  );
  const submittedLabel =
    headerDateLabel === 'Not available' ? 'Submitted date not available' : `Submitted ${headerDateLabel}`;

  if (isLoading) {
    return (
      <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: 'calc(100vh - 7.5rem)',
            overflow: 'hidden',
            borderRadius: '28px',
            border: '1px solid #dbe6eb',
            bgcolor: '#fff',
            p: { xs: 3, md: 4 },
          }}
        >
          <Skeleton variant="rounded" height={34} width={320} sx={{ borderRadius: '12px' }} />
          <Skeleton variant="rounded" height={176} sx={{ mt: 3, borderRadius: '20px' }} />
          <Skeleton variant="rounded" height={560} sx={{ mt: 3, borderRadius: '20px' }} />
        </Paper>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: 'calc(100vh - 7.5rem)',
            borderRadius: '28px',
            border: '1px solid #dbe6eb',
            bgcolor: '#fff',
            p: { xs: 3, md: 4 },
          }}
        >
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '26px', color: '#0f172a' }}>
            Unable to load response preview
          </Typography>
          <Typography sx={{ mt: 1.5, fontFamily: 'Arial, sans-serif', fontSize: '16px', color: '#667085' }}>
            {(error as Error | undefined)?.message || 'Please try again.'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(sourcePath)}
            sx={{
              mt: 3,
              borderRadius: '14px',
              bgcolor: '#25a8da',
              fontFamily: 'Inter, sans-serif',
              '&:hover': { bgcolor: '#1498ca' },
            }}
          >
            Back
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!instanceResponse || (flattenedResponses.length === 0 && !submittedNotes)) {
    return (
      <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: 'calc(100vh - 7.5rem)',
            borderRadius: '28px',
            border: '1px solid #dbe6eb',
            bgcolor: '#fff',
            p: { xs: 3, md: 4 },
          }}
        >
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '26px', color: '#0f172a' }}>
            Response not found
          </Typography>
          <Typography sx={{ mt: 1.5, fontFamily: 'Arial, sans-serif', fontSize: '16px', color: '#667085' }}>
            The selected questionnaire response is not available.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(sourcePath)}
            sx={{
              mt: 3,
              borderRadius: '14px',
              bgcolor: '#25a8da',
              fontFamily: 'Inter, sans-serif',
              '&:hover': { bgcolor: '#1498ca' },
            }}
          >
            Back
          </Button>
        </Paper>
      </Box>
    );
  }

  const statusLabel =
    instanceResponse.status === 'in_progress'
      ? 'In Progress'
      : instanceResponse.status === 'pending'
        ? 'Pending'
        : 'Completed';

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ px: { xs: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fafcfe',
              px: 3,
              py: 1.5,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Link
                component={RouterLink}
                underline="hover"
                color="inherit"
                to="/questionnaires"
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
              >
                Questionnaires
              </Link>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Response Preview
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 3, md: 4 }, pb: { xs: 4, md: 5 }, pt: 4 }}>
          {resolvedClientId ? (
            <MealSuggestionsClientSummaryStrip
              client={client}
              clientId={resolvedClientId}
              title={(clientName, clientNameLink) => (
                <>
                  {questionnaireTitle} for {clientNameLink || clientName}{' '}
                  <span className="whitespace-normal font-semibold text-white/95">
                    ({submittedLabel})
                  </span>
                </>
              )}
              headerAside={(
                <Tag
                  variant="success"
                  size="md"
                  className="h-[30px] bg-[#E2F7F0] font-[Inter,sans-serif] text-xs font-semibold text-[#066B55]"
                >
                  {statusLabel}
                </Tag>
              )}
            />
          ) : null}

          <Box sx={{ mt: 3, display: 'grid', gap: 1.5 }}>
            {groupedResponses.length === 0 && !submittedNotes ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '18px',
                  border: '1px solid #dbe6eb',
                  bgcolor: '#fff',
                  px: 3,
                  py: 4,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '20px', color: '#0f172a' }}>
                  No answers recorded
                </Typography>
                <Typography sx={{ mt: 1, fontFamily: 'Arial, sans-serif', fontSize: '15px', color: '#667085' }}>
                  This questionnaire instance does not include any submitted answers.
                </Typography>
              </Paper>
            ) : (
              <>
              {groupedResponses.map((group) => {
                const Icon = group.icon;
                const expanded = expandedGroups.includes(group.id);

                return (
                  <Paper
                    key={group.id}
                    elevation={0}
                    sx={{
                      overflow: 'hidden',
                      borderRadius: '18px',
                      border: '1px solid #dbe6eb',
                      bgcolor: '#fff',
                    }}
                  >
                    <Box
                      onClick={() => toggleGroup(group.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        px: 2.25,
                        py: 1.8,
                        cursor: 'pointer',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: expanded ? alpha(group.color, 0.16) : group.iconBg,
                            color: expanded ? group.color : group.iconColor,
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ fontSize: 15 }} />
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 700,
                            fontSize: '14px',
                            lineHeight: '20px',
                            letterSpacing: '0.41px',
                            color: expanded ? group.color : '#475467',
                            textTransform: 'uppercase',
                          }}
                        >
                          {group.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            fontSize: '12px',
                            lineHeight: '16px',
                            color: '#98a2b3',
                          }}
                        >
                          {group.items.length} question{group.items.length === 1 ? '' : 's'}
                        </Typography>
                      </Box>

                      <ExpandMoreRoundedIcon
                        sx={{
                          fontSize: 18,
                          color: expanded ? group.color : '#98a2b3',
                          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                        }}
                      />
                    </Box>

                    {expanded ? (
                      <Box sx={{ borderTop: '1px solid #eef2f6' }}>
                        {group.items.map((item, index) => (
                          <Box
                            key={`${group.id}-${item.questionId}`}
                            sx={{
                              px: 2.25,
                              py: 1.8,
                              borderBottom: index === group.items.length - 1 ? 'none' : '1px solid #eef2f6',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '20px',
                                color: '#99A1AF',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.6,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Box component="span">{item.questionTextResolved}</Box>
                              <Box component="span" sx={{ color: '#98a2b3' }}>•</Box>
                              <Box component="span" sx={{ color: '#98a2b3' }}>
                                {formatQuestionType(item.typeResolved)}
                              </Box>
                            </Typography>
                            <Typography
                              sx={{
                                mt: 1,
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                                fontSize: '15px',
                                lineHeight: '22px',
                                color: '#111827',
                              }}
                            >
                              {item.answerPreview}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : null}
                  </Paper>
                );
              })}
              {submittedNotes ? (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: '18px',
                    border: '1px solid #dbe6eb',
                    bgcolor: '#fff',
                    px: 2.25,
                    py: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '0.41px',
                      color: '#475467',
                      textTransform: 'uppercase',
                    }}
                  >
                    Notes
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1.25,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '15px',
                      lineHeight: '22px',
                      color: '#111827',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {submittedNotes}
                  </Typography>
                </Paper>
              ) : null}
              </>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default QuestionnaireResponsePreviewPage;
